import { createContext, useState, useEffect, useCallback, useRef, useContext } from 'react';
import axios from 'axios';
import { domain } from './helpers';
import { AuthContext } from './auth';

const CartContext = createContext(null);

const sanitizeItem = (item) => {
    if (!item || typeof item !== 'object') return null;
    const price = parseFloat(item.price);
    const rawQty = item.quantity == null ? 1 : item.quantity;
    const qty = parseInt(rawQty, 10);
    if (!Number.isFinite(price) || price < 0 || !Number.isInteger(qty) || qty < 1) return null;
    const stock = item.stock != null ? parseInt(item.stock, 10) : null;
    return {
        id: item.id,
        product_code: String(item.product_code || item.code || '').slice(0, 50),
        item_name: String(item.item_name || item.name || '').slice(0, 500),
        item_description: String(item.item_description || item.description || '').slice(0, 500),
        photo: String(item.photo || item.image || ''),
        price,
        stock: Number.isInteger(stock) && stock >= 0 ? stock : null,
        quantity: Math.min(qty, stock ?? 999, 999)
    };
};

const mergeItems = (existing, guest) => {
    const merged = [...existing];
    for (const guestItem of guest) {
        const idx = merged.findIndex(e => e.id === guestItem.id);
        if (idx >= 0) {
            merged[idx] = { ...merged[idx], quantity: Math.min(Math.max(merged[idx].quantity, guestItem.quantity), 999) };
        } else {
            merged.push(guestItem);
        }
    }
    return merged;
};

function CartProvider({ children }) {
    const [cart, setCart] = useState([]);
    const [cartTotal, setCartTotal] = useState(0);
    const [orderId, setOrderId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cartWarning, setCartWarning] = useState(null);

    const cartRef = useRef(cart);
    const orderIdRef = useRef(orderId);
    const syncTimeoutRef = useRef(null);

    useEffect(() => { cartRef.current = cart; }, [cart]);
    useEffect(() => { orderIdRef.current = orderId; }, [orderId]);

    const { profile } = useContext(AuthContext);
    const profileRef = useRef(profile);
    useEffect(() => { profileRef.current = profile; }, [profile]);

    // ─── createOrder ────────────────────────────────────────────────────────────
    const createOrder = useCallback(async () => {
        const currentProfile = profileRef.current;
        if (!currentProfile?.oauth_uid) {
            console.warn('No user logged in, cannot create order');
            return null;
        }
        try {
            setLoading(true);
            const res = await axios.post(`${domain}/api/order/create`, {
                oauth_uid: currentProfile.oauth_uid,
            });
            const newOrderId = res.data.order_number;
            localStorage.setItem('orderId', newOrderId);
            localStorage.setItem('cart', '[]');
            setOrderId(newOrderId);
            orderIdRef.current = newOrderId;
            return newOrderId;
        } catch (error) {
            console.error('Failed to create order:', error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // ─── syncCartToDatabase ──────────────────────────────────────────────────────
    const syncCartToDatabase = useCallback(async (overrideOrderId) => {
        const currentCart = cartRef.current;
        const currentOrderId = overrideOrderId ?? orderIdRef.current;
        const currentProfile = profileRef.current;

        if (!currentProfile?.oauth_uid || !currentOrderId) return;

        try {
            setLoading(true);
            // Full replace: releases old reservations, deletes, then re-inserts.
            // This correctly handles removals — items not in currentCart won't be re-reserved.
            await axios.put(`${domain}/api/order/sync`, {
                order_number: currentOrderId,
                oauth_uid: currentProfile.oauth_uid,
                items: currentCart.map(item => ({ id: item.id, quantity: item.quantity })),
            }, { withCredentials: true });
        } catch (error) {
            console.error('Failed to sync cart to database:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // ─── validateCartStock ───────────────────────────────────────────────────────
    const validateCartStock = useCallback(async (currentOrderId) => {
        const current = cartRef.current;
        if (current.length === 0) return;

        try {
            const checked = await Promise.all(
                current.map(async item => {
                    try {
                        const res = await axios.get(`${domain}/api/product/${item.id}`);
                        const available = res.data?.available_stock ?? res.data?.[0]?.available_stock ?? item.quantity;
                        return { ...item, stock: available, quantity: Math.min(item.quantity, available) };
                    } catch {
                        return item;
                    }
                })
            );

            const valid = checked.filter(i => i.quantity > 0);
            const removed = checked.filter(i => i.quantity <= 0).map(i => i.item_name || i.product_code);
            const trimmed = checked.filter(i => {
                const orig = current.find(c => c.id === i.id);
                return i.quantity > 0 && orig && i.quantity < orig.quantity;
            }).map(i => i.item_name || i.product_code);

            if (removed.length > 0 || trimmed.length > 0) {
                const parts = [];
                if (removed.length > 0) parts.push(`${removed.join(', ')} ${removed.length === 1 ? 'is' : 'are'} no longer available and ${removed.length === 1 ? 'has' : 'have'} been removed from your cart`);
                if (trimmed.length > 0) parts.push(`the quantity of ${trimmed.join(', ')} has been reduced to match available stock`);
                setCartWarning('Some items were updated: ' + parts.join('; ') + '.');

                setCart(valid);
                cartRef.current = valid;
                if (currentOrderId) await syncCartToDatabase(currentOrderId);
            }
        } catch (err) {
            console.error('Stock validation failed:', err);
        }
    }, [syncCartToDatabase]);

    // ─── Load cart on mount / profile change ────────────────────────────────────
    useEffect(() => {
        async function tryLoadOrder() {
            try {
                // On every profile change, release the previous order's reservation
                // (covers logout, and switching between users on the same device)
                const prevOrderId = orderIdRef.current;
                const prevUid = profileRef.current?.oauth_uid;
                if (prevOrderId && prevUid && prevUid !== profile?.oauth_uid) {
                    try {
                        await axios.post(`${domain}/api/order/release-reservation`, {
                            order_number: prevOrderId,
                            oauth_uid: prevUid,
                        }, { withCredentials: true });
                    } catch (err) {
                        console.warn('Failed to release previous cart reservation:', err);
                    }
                    setCart([]);
                    setOrderId(null);
                    orderIdRef.current = null;
                    localStorage.removeItem('cart');
                    localStorage.removeItem('orderId');
                }

                if (profile !== null) {
                    // Grab any guest cart before touching localStorage keys
                    const guestItems = (() => {
                        try {
                            const raw = localStorage.getItem('guestCart');
                            return raw ? JSON.parse(raw).map(sanitizeItem).filter(Boolean) : [];
                        } catch { return []; }
                    })();

                    const saved = localStorage.getItem('cart');
                    const savedOrderId = localStorage.getItem('orderId');

                    let resolvedOrderId;
                    let restoredCart = [];

                    if (!savedOrderId || savedOrderId === 'null') {
                        resolvedOrderId = await createOrder();
                    } else {
                        const response = await axios.get(`${domain}/api/order/expiry`, {
                            params: { order_number: savedOrderId, user_oauth_uid: profile.oauth_uid },
                            transformResponse: (data) => JSON.parse(data),
                        });

                        if (response.data === 0) {
                            const parsed = JSON.parse(saved);
                            restoredCart = Array.isArray(parsed)
                                ? parsed.map(sanitizeItem).filter(Boolean)
                                : [];
                            setOrderId(savedOrderId);
                            orderIdRef.current = savedOrderId;
                            resolvedOrderId = savedOrderId;
                        } else {
                            resolvedOrderId = await createOrder();
                        }
                    }

                    if (guestItems.length > 0) {
                        const merged = mergeItems(restoredCart, guestItems);
                        setCart(merged);
                        cartRef.current = merged;
                        localStorage.removeItem('guestCart');
                        await syncCartToDatabase(resolvedOrderId);
                        await validateCartStock(resolvedOrderId);
                    } else {
                        setCart(restoredCart);
                    }
                } else {
                    // Guest — load from guestCart localStorage
                    try {
                        const raw = localStorage.getItem('guestCart');
                        const parsed = raw ? JSON.parse(raw).map(sanitizeItem).filter(Boolean) : [];
                        setCart(parsed);
                    } catch {
                        setCart([]);
                    }
                }
            } catch (err) {
                console.warn('Failed to load cart:', err);
                setCart([]);
            }
        }
        tryLoadOrder();
    }, [profile, createOrder, syncCartToDatabase, validateCartStock]);

    // ─── Persist cart whenever it changes ───────────────────────────────────────
    useEffect(() => {
        const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
        setCartTotal(total);
        if (profileRef.current !== null) {
            localStorage.setItem('cart', JSON.stringify(cart));
        } else {
            localStorage.setItem('guestCart', JSON.stringify(cart));
        }
    }, [cart]);

    // ─── debouncedSync ───────────────────────────────────────────────────────────
    const debouncedSync = useCallback((overrideOrderId) => {
        if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = setTimeout(() => {
            syncCartToDatabase(overrideOrderId);
        }, 1000);
    }, [syncCartToDatabase]);

    // ─── addToCart ───────────────────────────────────────────────────────────────
    const addToCart = useCallback(async (product) => {
        const sanitized = sanitizeItem(product);
        if (!sanitized) return;

        setCart(prev => {
            const existing = prev.find(item => item.id === sanitized.id);
            return existing
                ? prev.map(item =>
                    item.id === sanitized.id
                        ? { ...item, quantity: Math.min(item.quantity + 1, item.stock ?? 999, 999) }
                        : item
                )
                : [...prev, sanitized];
        });

        // Only sync to DB when logged in
        if (profileRef.current?.oauth_uid) {
            let currentOrderId = orderIdRef.current;
            if (!currentOrderId) {
                currentOrderId = await createOrder();
            }
            if (currentOrderId) {
                debouncedSync(currentOrderId);
            }
        }
    }, [createOrder, debouncedSync]);

    // ─── removeFromCart ──────────────────────────────────────────────────────────
    const removeFromCart = useCallback((productId) => {
        setCart(prev => prev.filter(item => item.id !== productId));
        if (profileRef.current?.oauth_uid && orderIdRef.current) {
            debouncedSync(orderIdRef.current);
        }
    }, [debouncedSync]);

    // ─── updateQuantity ──────────────────────────────────────────────────────────
    const updateQuantity = useCallback((productId, quantity) => {
        const qty = parseInt(quantity, 10);
        if (!Number.isInteger(qty) || qty < 0) return;

        if (qty === 0) {
            removeFromCart(productId);
        } else {
            setCart(prev =>
                prev.map(item => {
                    if (item.id !== productId) return item;
                    const max = item.stock != null ? Math.min(item.stock, 999) : 999;
                    return { ...item, quantity: Math.min(qty, max) };
                })
            );
            if (profileRef.current?.oauth_uid && orderIdRef.current) {
                debouncedSync(orderIdRef.current);
            }
        }
    }, [removeFromCart, debouncedSync]);

    // ─── clearCart ───────────────────────────────────────────────────────────────
    const clearCart = useCallback(async () => {
        const currentOrderId = orderIdRef.current;
        const currentProfile = profileRef.current;

        // Release DB reservation before clearing so stock is immediately available again
        if (currentOrderId && currentProfile?.oauth_uid) {
            try {
                await axios.post(`${domain}/api/order/release-reservation`, {
                    order_number: currentOrderId,
                    oauth_uid: currentProfile.oauth_uid,
                }, { withCredentials: true });
            } catch (err) {
                console.warn('Failed to release cart reservation on clear:', err);
            }
        }

        setCart([]);
        setOrderId(null);
        orderIdRef.current = null;
        localStorage.removeItem('cart');
        localStorage.removeItem('orderId');
    }, []);

    const getItemCount = useCallback(
        () => cart.reduce((count, item) => count + item.quantity, 0),
        [cart]
    );

    const forceSyncToDatabase = useCallback(async () => {
        if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
        await syncCartToDatabase();
    }, [syncCartToDatabase]);

    const getOrderId = useCallback(async () => {
        if (orderIdRef.current) return orderIdRef.current;
        if (!profileRef.current?.oauth_uid) return null;
        return await createOrder();
    }, [createOrder]);

    const dismissCartWarning = useCallback(() => setCartWarning(null), []);

    return (
        <CartContext.Provider value={{
            cart,
            cartTotal,
            orderId,
            loading,
            cartWarning,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            getItemCount,
            forceSyncToDatabase,
            getOrderId,
            syncCartToDatabase,
            dismissCartWarning,
        }}>
            {children}
        </CartContext.Provider>
    );
}

export { CartContext, CartProvider };
